import {
  ReactNode,
  Dispatch,
  SetStateAction,
  ChangeEvent,
  useState,
  useEffect,
} from 'react';
import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import { AccountModal } from './AccountModal';
import { useTranslation } from '../../../contexts/TranslationContext';
import { MHMTypography } from '../MHM/MHMTypography';
import { deviceListStateAtom } from '../../../models/atoms/device_atoms';

export type AccountPermissions = {
  view: boolean | string[];
  install: boolean;
  admin: boolean;
};

type Props = {
  label: ReactNode;
  required?: boolean;
  permissions: AccountPermissions;
  sso_permissions: boolean;
  onPermissionsChange: Dispatch<SetStateAction<AccountPermissions>>;
  disabled?: boolean;
};

const SELECT_ALL_VALUE = '__all__';

export function AccountModalPermissions(props: Props) {
  const {
    label,
    required,
    permissions,
    sso_permissions,
    onPermissionsChange,
    disabled,
  } = props;
  const { t } = useTranslation();
  const deviceList = useRecoilValue(deviceListStateAtom);

  const disableView = permissions.admin || permissions.install;
  const disableInstall = permissions.admin;

  const allDeviceIds: string[] = useMemo(() => {
    return deviceList.devices
      .map(device => device.device_id)
      .sort((a, b) => parseInt(a) - parseInt(b));
  }, [deviceList]);

  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);

  const allDevicesSelected = selectedDevices.length === allDeviceIds.length;
  const isIndeterminate = selectedDevices.length !== allDeviceIds.length;

  const showDeviceSelector =
    permissions.view && !permissions.install && !permissions.admin;
  const hasDevices = allDeviceIds.length > 0;

  useEffect(() => {
    setSelectedDevices(
      (Array.isArray(permissions.view) ? permissions.view : allDeviceIds)
        .slice()
        .map(id => id.toString())
        .sort((a, b) => parseInt(a) - parseInt(b)),
    );
  }, [permissions.view, allDeviceIds]);

  // Enforce cascading permissions:
  // - Admin: admin + install + view
  // - Install: install + view
  // - View: view only
  function onViewChange(_e: ChangeEvent<HTMLInputElement>, checked: boolean) {
    // If higher-level permissions are selected, view cannot be unchecked.
    if (!checked && (permissions.install || permissions.admin)) {
      return;
    }
    onPermissionsChange(prev => ({
      ...prev,
      view: checked,
      install: false,
      admin: false,
    }));
  }

  function onInstallChange(
    _e: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) {
    // If admin is selected, ignore toggling install directly.
    if (permissions.admin) {
      return;
    }
    if (checked) {
      onPermissionsChange(prev => ({
        ...prev,
        view: true,
        install: true,
        admin: false,
      }));
    } else {
      // Unchecking install keeps existing view selection
      onPermissionsChange(prev => ({
        ...prev,
        install: false,
        admin: false,
      }));
    }
  }

  function onAdminChange(_e: ChangeEvent<HTMLInputElement>, checked: boolean) {
    if (checked) {
      onPermissionsChange(prev => ({
        ...prev,
        view: true,
        install: true,
        admin: true,
      }));
    } else {
      // Dropping admin preserves install + view selections
      onPermissionsChange(prev => ({
        ...prev,
        view: true,
        install: true,
        admin: false,
      }));
    }
  }

  function handleDeviceSelection(event: SelectChangeEvent<string[]>) {
    const value = event.target.value;
    const rawSelection = typeof value === 'string' ? value.split(',') : value;

    if (rawSelection.includes(SELECT_ALL_VALUE)) {
      if (rawSelection.length === allDeviceIds.length + 1) {
        setSelectedDevices([]);
      } else {
        setSelectedDevices([...allDeviceIds]);
      }
      return;
    }

    const selection = rawSelection.filter(
      deviceId => deviceId !== SELECT_ALL_VALUE,
    );

    setSelectedDevices(selection.sort((a, b) => parseInt(a) - parseInt(b)));
  }

  function handleSelectClose() {
    if (selectedDevices.length === 0) {
      onPermissionsChange(prev => ({
        ...prev,
        view: false,
      }));
    } else if (selectedDevices.length === allDeviceIds.length) {
      onPermissionsChange(prev => ({
        ...prev,
        view: true,
      }));
    } else {
      onPermissionsChange(prev => ({
        ...prev,
        view: [...selectedDevices],
      }));
    }
  }

  if (sso_permissions) {
    return (
      <AccountModal.FormGroup>
        <AccountModal.FormLabel
          label={label}
          labelProps={{ fontSize: 20 }}
          required={required}
        >
          <MHMTypography>
            {t('Permissions for this account are managed through SSO')}
          </MHMTypography>
        </AccountModal.FormLabel>
      </AccountModal.FormGroup>
    );
  }

  const selectLabelId = 'account-permissions-view-devices';

  return (
    <AccountModal.FormGroup>
      <AccountModal.FormLabel
        label={label}
        labelProps={{ fontSize: 20 }}
        required={required}
      >
        <AccountModal.LabelledCheckbox
          checked={Boolean(permissions.view)}
          disabled={disableView || disabled}
          onChange={onViewChange}
          label={
            <>
              {t('View')}
              &nbsp;-&nbsp;
              {t(
                "can view device data, make notes and add labels, can't view accounts or modify device settings",
              )}
            </>
          }
        />
        {showDeviceSelector && (
          <Stack gap={1} sx={{ pl: 4 }}>
            {hasDevices ? (
              <FormControl fullWidth>
                <InputLabel id={selectLabelId}>
                  {t('Allowed Devices')}
                </InputLabel>
                <Select
                  onClose={handleSelectClose}
                  labelId={selectLabelId}
                  multiple
                  value={selectedDevices}
                  onChange={handleDeviceSelection}
                  label={t('Allowed Devices')}
                  renderValue={selected =>
                    selected.length
                      ? selected.join(', ')
                      : t('No devices selected')
                  }
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: 200 },
                    },
                  }}
                >
                  <MenuItem
                    value={SELECT_ALL_VALUE}
                    disabled={!hasDevices}
                    sx={theme => ({
                      'flex': 1,
                      'position': 'sticky',
                      'top': 0,
                      'zIndex': theme.zIndex.modal + 1,
                      'bgcolor': theme.palette.background.paper,
                      'borderBottom': `1px solid ${theme.palette.divider}`,
                      'boxShadow': theme.shadows[2],
                      '&:hover': {
                        bgcolor: theme.palette.background.paper,
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: theme.palette.background.paper,
                        pointerEvents: 'none',
                        zIndex: -1,
                      },
                    })}
                  >
                    <Checkbox
                      indeterminate={isIndeterminate}
                      checked={allDevicesSelected}
                    />
                    <ListItemText primary={t('Select All Devices')} />
                  </MenuItem>
                  {allDeviceIds.map(deviceId => (
                    <MenuItem key={deviceId} value={deviceId}>
                      <Checkbox checked={selectedDevices.includes(deviceId)} />
                      <ListItemText primary={deviceId} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <MHMTypography color="text.secondary">
                {t('No devices available')}
              </MHMTypography>
            )}
            {hasDevices && selectedDevices.length === 0 && (
              <MHMTypography color="error">
                {t('Select at least one device to grant access')}
              </MHMTypography>
            )}
          </Stack>
        )}
        <AccountModal.LabelledCheckbox
          checked={permissions.install}
          disabled={disableInstall || disabled}
          onChange={onInstallChange}
          label={
            <>
              {t('Install')}
              &nbsp;-&nbsp;
              {t(
                'all permissions associated with View, additionally able to setup devices via mobile app',
              )}
            </>
          }
        />
        <AccountModal.LabelledCheckbox
          checked={permissions.admin}
          onChange={onAdminChange}
          disabled={disabled}
          label={
            <>
              {t('Admin')}
              &nbsp;-&nbsp;
              {t(
                'all permissions associated with Install, additionally able to view, create and manage accounts, as well as adjust device settings',
              )}
            </>
          }
        />
      </AccountModal.FormLabel>
    </AccountModal.FormGroup>
  );
}
